<template>
  <aside class="doc-shell">
    <EmptyState
      v-if="!store.detail"
      title="No conversation selected"
      sub="The written-up requirement will appear here once drafted."
    />

    <template v-else>
      <header class="doc-head">
        <div class="doc-head-left">
          <StatusPill :status="store.detail.status" />
          <span v-if="store.activeDocument" class="doc-version">
            v{{ store.activeDocument.version }}
          </span>
        </div>
        <div class="doc-head-right" v-if="store.activeDocument">
          <button class="ghost-btn" title="Copy markdown" @click="copyMd">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
          <a class="ghost-btn" :href="downloadHref" download>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            .md
          </a>
        </div>
      </header>

      <EmptyState
        v-if="!store.activeDocument"
        :title="emptyContent.title"
        :sub="emptyContent.sub"
      />

      <div v-else class="doc-body">
        <h1 class="doc-title">{{ store.activeDocument.title }}</h1>
        <div class="doc-md" v-html="renderedHtml" />
      </div>

      <footer v-if="store.activeDocument" class="doc-foot">
        <template v-if="!store.detail.latest_prd_confirmed">
          <button class="primary-btn confirm" @click="onConfirm" :disabled="confirming">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {{ confirming ? "Confirming…" : "Confirm requirement" }}
          </button>
          <div class="doc-hint">
            Locks the requirement. Status → <strong>Confirmed</strong>.
          </div>
        </template>
        <div v-else class="doc-confirmed" :style="confirmedStyle">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>Confirmed · ready for development</span>
        </div>
      </footer>
    </template>
  </aside>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { ElMessage } from "element-plus";
import { marked } from "marked";
import DOMPurify from "dompurify";
import StatusPill from "@/components/design/StatusPill.vue";
import EmptyState from "@/components/design/EmptyState.vue";
import { STATUS_META } from "@/components/design/statusMeta";
import { useConversationStore } from "@/stores/conversation";
import { downloadDocumentUrl } from "@/api/documents";
import type { ConversationStatus } from "@/types/api";

const store = useConversationStore();
const confirming = ref(false);

const EMPTY_CONTENT: Partial<Record<ConversationStatus, { title: string; sub: string }>> = {
  needs_it_review: {
    title: "Escalated to IT",
    sub: "Bring the agent's notes to the dev team before drafting.",
  },
  rejected: {
    title: "Requirement not approved",
    sub: "No write-up will be produced. See the conversation for the reason.",
  },
  prd_pending_confirm: {
    title: "Drafting in progress…",
    sub: "Continue the discussion — the agent will draft one when the scope is clear.",
  },
};
const DEFAULT_EMPTY = {
  title: "Nothing drafted yet",
  sub: "Continue the discussion — the agent will draft one when the scope is clear.",
};

const emptyContent = computed(() => {
  const s = store.detail?.status as ConversationStatus | undefined;
  return (s && EMPTY_CONTENT[s]) ?? DEFAULT_EMPTY;
});

const renderedHtml = computed(() => {
  if (!store.activeDocument) return "";
  return DOMPurify.sanitize(
    marked.parse(store.activeDocument.content_md, { breaks: true }) as string
  );
});

const downloadHref = computed(() =>
  store.activeDocument ? downloadDocumentUrl(store.activeDocument.id) : "#"
);

const confirmedStyle = computed(() => ({
  background: STATUS_META.prd_confirmed.bg,
  color: STATUS_META.prd_confirmed.fg,
}));

async function onConfirm() {
  confirming.value = true;
  try {
    await store.confirmPRD();
    ElMessage.success("Requirement confirmed.");
  } catch (e: any) {
    ElMessage.error(`Confirm failed: ${e?.response?.data?.detail ?? e?.message ?? e}`);
  } finally {
    confirming.value = false;
  }
}

async function copyMd() {
  if (!store.activeDocument) return;
  try {
    await navigator.clipboard.writeText(store.activeDocument.content_md);
    ElMessage.success("Copied to clipboard.");
  } catch {
    ElMessage.error("Copy failed.");
  }
}
</script>

<style scoped>
.doc-shell {
  background: var(--surface);
  border-left: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  height: 100%;
}
.doc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  flex-shrink: 0;
}
.doc-head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.doc-head-right {
  display: flex;
  gap: 6px;
}
.doc-version {
  font-size: 11.5px;
  color: var(--ink-4);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.doc-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  min-height: 0;
}
.doc-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 22px;
  letter-spacing: -0.02em;
  line-height: 1.25;
}
.doc-md :deep(h1) {
  display: none;
}
.doc-md :deep(h2) {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  margin: 26px 0 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border);
  letter-spacing: -0.005em;
}
.doc-md :deep(h2:first-child) {
  margin-top: 0;
}
.doc-md :deep(h3) {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-2);
  margin: 16px 0 6px;
}
.doc-md :deep(p) {
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.7;
  margin: 6px 0;
}
.doc-md :deep(ul),
.doc-md :deep(ol) {
  padding-left: 20px;
  margin: 6px 0;
}
.doc-md :deep(li) {
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.7;
  margin: 4px 0;
}
.doc-md :deep(strong) {
  color: var(--ink);
  font-weight: 600;
}
.doc-md :deep(code) {
  background: var(--hover);
  padding: 0 4px;
  border-radius: 3px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}
.doc-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--border);
  background: var(--surface);
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}
.doc-hint {
  font-size: 11.5px;
  color: var(--ink-4);
  text-align: center;
}
.doc-confirmed {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
}
.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: var(--ink-3);
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.12s, color 0.12s;
}
.ghost-btn:hover {
  background: var(--hover);
  color: var(--ink);
}
.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--ink);
  color: #fff;
  border: none;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s, opacity 0.15s;
}
.primary-btn:hover:not(:disabled) {
  background: #1a1f26;
}
.primary-btn:active:not(:disabled) {
  transform: scale(0.98);
}
.primary-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.confirm {
  width: 100%;
}
</style>
